import { createSlice } from "@reduxjs/toolkit";
import StorageManager from "../../utils/StorageManager";

/**
 * holdAndWinSlice — Cleopatra's Fire Hold & Win ("Cleopatra's Respins").
 *
 * A STANDALONE slice (store key `holdAndWin`): every new feature owns its
 * state, nothing merged into featureGame. Modelled on potCollectionSlice —
 * fed by rgsPlay.fulfilled for the trigger and every respin, and by
 * initializeGame / refreshGameData for a reload — but it carries only what
 * THIS game's back end sends.
 *
 * ── The server decides, this stores (GDD invariant 4) ──
 *
 * Every response in the feature carries the WHOLE H&W state in
 * features.bonus_data; RGS/PopiPlay/holdAndWinState.js parses it into the
 * `holdAndWin` field of the result. This slice keeps:
 *
 *   the grid        kinds / values, SCREEN rows
 *                   (row 0 = top — contract §14.1 mapping already applied)
 *   the counters    respinsLeft, unlockedRows, accumulatedPayout
 *   the flags       royal, win limit (the end sequence needs both)
 *   the last STEP   newCells / updatedCells / scarabs / win / returnState —
 *                   what the control has to animate for this response.
 *                   `stepId` changes on every response, so a consumer can
 *                   tell a new step from a re-render.
 *
 * ── Lifecycle ──
 *
 *   triggered   a spin/freespin entered H&W (state `respin`). The trigger spin
 *               still presents; the intro and the transition follow.
 *   active      the H&W room is on screen. Raised at the transition's
 *               midpoint (setHoldAndWinActive), like freeSpins.active.
 *   phase       'none' | 'intro' | 'respins' | 'settled' — where the round is.
 *   fromFreeSpins  the trigger was a free spin: the exit goes BACK to the free
 *               spins room, not to the base game (contract §8.4).
 *
 * resetHoldAndWin clears it all once the exit transition has handed back.
 */

const emptyStep = {
  phase: null, // 'trigger' | 'respin' | 'settled' | 'restore'
  newCells: [],
  updatedCells: [],
  scarabs: null,
  win: 0,
  returnState: null,
};

const initialState = {
  triggered: false,
  active: false,
  phase: "none",
  fromFreeSpins: false,
  // 'none' | 'intro' (reloaded on the trigger, nothing played) |
  // 'in_progress' (reloaded mid-feature). Cleared when the room takes over.
  reloadPhase: "none",
  // The mid-feature reload popup (YOU HAVE / n / RESPIN(S) LEFT) is up.
  // Raised by HoldAndWinControlContext, cleared by the popup's close.
  showReloadPopup: false,
  // The round's Total Win popup (Mad Labs' total_win, H&W's amount) is up.
  // Raised after the End Collector, cleared by the popup's close.
  showTotalWin: false,

  respinsLeft: 0,
  unlockedRows: 4,
  // Symbols standing in the active zone — what the next row unlock counts.
  cellsInZone: 0,
  // The trigger came from the Random Pot (storage.random_pot) — its coins are
  // not on the paid board; a presentation spin lands them (Games/Shared/
  // randomPot, ReelControlContext).
  randomPot: false,
  accumulatedPayout: 0,
  royalJackpot: false,
  winLimitReached: false,

  // [column][screenRow], 5 x 8.
  kinds: null,
  values: null,
  points: null, // raw points, for the jackpot coin (options.jackpots)
  // The PRODUCT of every scarab reaching each cell — 1s until the settling
  // respin reveals them. Server-authoritative; the reveal lands on it.
  scarabMultipliers: null,

  step: emptyStep,
  stepId: 0,

  // The base win of the spin that triggered (subunits) — part of the round's
  // TOTAL WIN. Live: the trigger response's round win; reload: the frozen
  // copy (StorageManager trigger_win, saved at the trigger).
  triggerWin: 0,
  // ── The finished round's TOTAL WIN, after the room has handed back ──
  // Pot Collection preserves its round through resetPotCollection and free
  // spins its displayedTotalWin through resetFreeSpins, so the base game's
  // win display keeps showing what the bonus paid until the next spin
  // (Vladimir, 2026-09-30: "user must see what he has won"). Kept through
  // resetHoldAndWin; cleared by clearHoldAndWinWin on the next spin.
  completedTotalWin: 0,
};

/** Copy the parsed server state into the slice. */
const applyServerState = (state, hw) => {
  state.respinsLeft = hw.respinsLeft ?? state.respinsLeft;
  state.unlockedRows = hw.unlockedRows ?? state.unlockedRows;
  state.cellsInZone = hw.cellsInZone ?? state.cellsInZone;
  if (hw.phase === "trigger") state.randomPot = !!hw.randomPot;
  state.accumulatedPayout = hw.accumulatedPayout ?? state.accumulatedPayout;
  state.royalJackpot = !!hw.royalJackpot;
  state.winLimitReached = !!hw.winLimitReached;
  state.kinds = hw.screen?.kinds ?? state.kinds;
  state.values = hw.screen?.values ?? state.values;
  state.points = hw.screen?.points ?? state.points;
  state.scarabMultipliers =
    hw.screen?.scarabMultipliers ?? state.scarabMultipliers;
  state.step = {
    phase: hw.phase,
    newCells: hw.newCells || [],
    updatedCells: hw.updatedCells || [],
    scarabs: hw.scarabs ?? null,
    win: hw.win ?? 0,
    returnState: hw.returnState ?? null,
  };
  state.stepId += 1;
};

/** Reload: rebuild from an init / refresh response (contract §9). */
const hydrateFromInit = (state, hw) => {
  if (!hw) return;
  applyServerState(state, hw);
  state.triggered = true;
  state.triggerWin = StorageManager.loadTriggerWin() || 0;
  // A free spin that triggered comes back with freespins_* beside bonus_data;
  // the parser does not see that, so the room-return is decided by the FS
  // slice's own hydrate. Kept false here; the control reads freeSpins.active.
  if (hw.restoreIntro) {
    // Reloaded on the trigger response: nothing played yet. The normal entry
    // runs — trigger spin, intro, transition — from the base (or FS) room.
    // A Random Pot trigger runs its Pot sequence again first (ReelControl-
    // Context) — the flag is only copied on a live trigger by applyServerState.
    state.randomPot = !!hw.randomPot;
    state.active = false;
    state.phase = "intro";
    state.reloadPhase = "intro";
  } else {
    // Mid-feature: the room is on screen from the first frame.
    state.active = true;
    state.phase = hw.respinsLeft === 0 ? "settled" : "respins";
    state.reloadPhase = "in_progress";
  }
};

const holdAndWinSlice = createSlice({
  name: "holdAndWin",
  initialState,
  reducers: {
    setHoldAndWinActive: (state, action) => {
      state.active = !!action.payload;
      if (state.active && state.phase === "intro") state.phase = "respins";
    },
    setHoldAndWinPhase: (state, action) => {
      state.phase = action.payload;
    },
    clearHoldAndWinReload: (state) => {
      state.reloadPhase = "none";
    },
    setShowHoldAndWinReload: (state, action) => {
      state.showReloadPopup = !!action.payload;
    },
    setShowHoldAndWinTotalWin: (state, action) => {
      state.showTotalWin = !!action.payload;
    },
    // resetHoldAndWin: () => initialState,
    // Everything cleared, EXCEPT the finished round's total — shown on the
    // base game's win display until the next spin (see completedTotalWin).
    // `{ keepTotal: false }` when the room hands back to FREE SPINS: their
    // own total win carries the bonus there.
    resetHoldAndWin: (state, action) => {
      const keepTotal = action?.payload?.keepTotal !== false;
      const paid = state.step?.win || state.accumulatedPayout || 0;
      return {
        ...initialState,
        completedTotalWin: keepTotal ? (state.triggerWin || 0) + paid : 0,
      };
    },
    // The next spin: the preserved total goes (clearPotCollectionWin's twin).
    clearHoldAndWinWin: (state) => {
      state.completedTotalWin = 0;
    },
  },
  extraReducers: (builder) => {
    // Inline requires, as potCollectionSlice does, to avoid a circular import.
    const { rgsPlay } = require("./spinSlice");
    const { initializeGame, refreshGameData } = require("./gameSettingsSlice");

    builder
      .addCase(rgsPlay.fulfilled, (state, action) => {
        const hw = action.payload?.holdAndWin;
        if (!hw) return;

        if (hw.phase === "trigger") {
          // ── Trigger ──
          // The Random Pot's presentation spin re-sends the trigger with a
          // board of its own and win 0 — the real trigger win came first.
          const keptTriggerWin = state.triggerWin;
          const fresh = { ...initialState };
          Object.assign(state, fresh);
          applyServerState(state, hw);
          state.triggered = true;
          state.phase = "intro";
          state.fromFreeSpins = hw.command === "freespin";
          state.triggerWin = action.payload?.round?.randomPotPresentation
            ? keptTriggerWin
            : action.payload?.round?.win ?? 0;
          return;
        }

        if (hw.phase === "respin" || hw.phase === "settled") {
          // ── A respin step ──
          applyServerState(state, hw);
          if (hw.phase === "settled") state.phase = "settled";
        }
      })
      .addCase(initializeGame.fulfilled, (state, action) => {
        hydrateFromInit(state, action.payload?.result?.holdAndWin);
      })
      .addCase(refreshGameData.fulfilled, (state, action) => {
        hydrateFromInit(state, action.payload?.result?.holdAndWin);
      });
  },
});

export const {
  setHoldAndWinActive,
  setHoldAndWinPhase,
  clearHoldAndWinReload,
  setShowHoldAndWinReload,
  setShowHoldAndWinTotalWin,
  resetHoldAndWin,
  clearHoldAndWinWin,
} = holdAndWinSlice.actions;

// ── Selectors ──
export const selectHoldAndWin = (state) => state.holdAndWin;
export const selectHoldAndWinTriggered = (state) => state.holdAndWin.triggered;
// The trigger round still owes its intro (live, or reloaded on the trigger).
// False mid-feature, so a round replayed there is never taken for a trigger.
export const selectHoldAndWinIntroPending = (state) =>
  state.holdAndWin.triggered && state.holdAndWin.phase === "intro";
export const selectHoldAndWinActive = (state) => state.holdAndWin.active;
export const selectHoldAndWinPhase = (state) => state.holdAndWin.phase;
export const selectHoldAndWinFromFreeSpins = (state) =>
  state.holdAndWin.fromFreeSpins;
export const selectHoldAndWinReloadPhase = (state) =>
  state.holdAndWin.reloadPhase;
export const selectShowHoldAndWinReload = (state) =>
  state.holdAndWin.showReloadPopup;
export const selectShowHoldAndWinTotalWin = (state) =>
  state.holdAndWin.showTotalWin;
// What the round PAID (outcome.win on the settling step, subunits, capped).
export const selectHoldAndWinWin = (state) => state.holdAndWin.step?.win ?? 0;
export const selectHoldAndWinRespinsLeft = (state) =>
  state.holdAndWin.respinsLeft;
export const selectHoldAndWinUnlockedRows = (state) =>
  state.holdAndWin.unlockedRows;
// Separate selectors — an object built per call would re-render every time.
export const selectHoldAndWinKinds = (state) => state.holdAndWin.kinds;
export const selectHoldAndWinValues = (state) => state.holdAndWin.values;
export const selectHoldAndWinPoints = (state) => state.holdAndWin.points;
export const selectHoldAndWinStep = (state) => state.holdAndWin.step;
export const selectHoldAndWinStepId = (state) => state.holdAndWin.stepId;
export const selectHoldAndWinPayout = (state) =>
  state.holdAndWin.accumulatedPayout;
// The finished round's TOTAL WIN, kept for the base game until the next spin.
export const selectHoldAndWinCompletedTotalWin = (state) =>
  state.holdAndWin.completedTotalWin;

export default holdAndWinSlice.reducer;
